import React from 'react';

import PropTypes from 'prop-types';

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@material-ui/core';

import LocaleMessage from '~/components/LocaleMessage';

import { getMapPointSelectionKey } from '../../lib/robotDeliveryPayload';
import { DarkDialog } from '../../styles';
import { StackedMissionPointList } from './rc3DialogLayouts';
import {
    containedMissionPointButtonSx,
    outlinedMissionPointButtonSx,
} from './rc3MissionUiConstants';

const deliveryMapPointShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    reference: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

/**
 * Diálogo: título + lista de pontos com seleção múltipla (ordem = ordem de toque) e chips de feedback.
 */
export default function Rc3StackedMapPointDialog({
    open,
    onClose,
    titleMsg,
    subtitleMsg,
    selectionHintMsg,
    emptyMsg,
    loadingMsg,
    loading,
    points,
    selectedPoints,
    onTogglePoint,
    onContinue,
    continueMsg,
    closeButtonMsg,
}) {
    function selectionKey(pose) {
        return getMapPointSelectionKey(pose);
    }

    function indexInSelection(pose) {
        const k = selectionKey(pose);
        return selectedPoints.findIndex(p => selectionKey(p) === k);
    }

    function renderSelectedChips() {
        if (!selectedPoints.length) {
            return null;
        }
        return (
            <Box
                style={{
                    marginBottom: 16,
                    padding: '12px 14px',
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                }}
            >
                <div
                    style={{
                        color: '#bdbdbd',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        marginBottom: 10,
                    }}
                >
                    <LocaleMessage msg="rc3.robot.data.delivery.selected_strip_label" />
                </div>
                <Box
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                    }}
                >
                    {selectedPoints.map((p, idx) => (
                        <Chip
                            key={selectionKey(p)}
                            label={`${idx + 1}. ${p.name || '---'}`}
                            onDelete={() => onTogglePoint(p)}
                            size="small"
                            style={{
                                color: '#fff',
                                backgroundColor: 'rgba(25, 118, 210, 0.45)',
                                border: '1px solid rgba(100, 181, 246, 0.5)',
                            }}
                        />
                    ))}
                </Box>
            </Box>
        );
    }

    function renderPointBody() {
        if (loading) {
            return (
                <div
                    className="text-center"
                    style={{
                        padding: '32px 0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 16,
                        color: '#ccc',
                    }}
                >
                    <CircularProgress color="primary" size={40} />
                    <span>
                        <LocaleMessage msg={loadingMsg} />
                    </span>
                </div>
            );
        }
        if (points.length === 0) {
            return (
                <span style={{ color: '#ccc' }}>
                    <LocaleMessage msg={emptyMsg} />
                </span>
            );
        }
        return (
            <>
                {renderSelectedChips()}
                <StackedMissionPointList>
                    {points.map((pose, idx) => {
                        const ord = indexInSelection(pose);
                        const selected = ord >= 0;
                        return (
                            <Button
                                key={
                                    pose.id
                                        ? `pt-${pose.id}`
                                        : `pt-${pose.name}-${idx}`
                                }
                                fullWidth
                                size="medium"
                                color="primary"
                                variant={selected ? 'contained' : 'outlined'}
                                style={
                                    selected
                                        ? {
                                              ...containedMissionPointButtonSx,
                                              boxShadow:
                                                  '0 0 0 2px rgba(100, 181, 246, 0.65)',
                                          }
                                        : outlinedMissionPointButtonSx
                                }
                                onClick={() => onTogglePoint(pose)}
                            >
                                <span
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 10,
                                        width: '100%',
                                    }}
                                >
                                    {selected ? (
                                        <span
                                            style={{
                                                flexShrink: 0,
                                                minWidth: 26,
                                                height: 26,
                                                borderRadius: '50%',
                                                background: 'rgba(0,0,0,0.25)',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.85rem',
                                                fontWeight: 700,
                                            }}
                                        >
                                            {ord + 1}
                                        </span>
                                    ) : null}
                                    <span
                                        style={{ textAlign: 'center', flex: 1 }}
                                    >
                                        {pose.name}
                                    </span>
                                </span>
                            </Button>
                        );
                    })}
                </StackedMissionPointList>
            </>
        );
    }

    const canContinue = !loading && selectedPoints.length > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
        >
            <DarkDialog>
                <DialogTitle
                    style={{
                        textTransform: 'uppercase',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <LocaleMessage msg={titleMsg} />
                </DialogTitle>
                <DialogContent
                    style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        overflowY: 'auto',
                        maxHeight: 'min(70vh, 640px)',
                        paddingTop: 8,
                    }}
                >
                    <div
                        className="text-center"
                        style={{
                            marginBottom: 12,
                            color: '#e0e0e0',
                            fontSize: '0.95rem',
                            textTransform: 'uppercase',
                            wordBreak: 'break-word',
                        }}
                    >
                        <LocaleMessage msg={subtitleMsg} />
                    </div>
                    <div
                        style={{
                            marginBottom: 16,
                            color: '#9e9e9e',
                            fontSize: '0.875rem',
                            lineHeight: 1.45,
                            textAlign: 'center',
                            wordBreak: 'break-word',
                        }}
                    >
                        <LocaleMessage msg={selectionHintMsg} />
                    </div>
                    {renderPointBody()}
                </DialogContent>
                <DialogActions
                    style={{
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        gap: 8,
                        padding: '12px 16px 16px',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <Button onClick={onClose} color="primary" autoFocus>
                        <LocaleMessage msg={closeButtonMsg} />
                    </Button>
                    <Button
                        onClick={onContinue}
                        color="primary"
                        variant="contained"
                        disabled={!canContinue}
                        style={{
                            ...containedMissionPointButtonSx,
                            minWidth: 160,
                        }}
                    >
                        <LocaleMessage msg={continueMsg} />
                    </Button>
                </DialogActions>
            </DarkDialog>
        </Dialog>
    );
}

Rc3StackedMapPointDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    titleMsg: PropTypes.string.isRequired,
    subtitleMsg: PropTypes.string.isRequired,
    selectionHintMsg: PropTypes.string.isRequired,
    emptyMsg: PropTypes.string.isRequired,
    loadingMsg: PropTypes.string,
    loading: PropTypes.bool,
    points: PropTypes.arrayOf(deliveryMapPointShape).isRequired,
    selectedPoints: PropTypes.arrayOf(deliveryMapPointShape).isRequired,
    onTogglePoint: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    continueMsg: PropTypes.string,
    closeButtonMsg: PropTypes.string,
};

Rc3StackedMapPointDialog.defaultProps = {
    closeButtonMsg: 'button.close',
    loadingMsg: 'rc3.robot.data.delivery.loading_map',
    loading: false,
    continueMsg: 'rc3.robot.data.delivery.continue',
};
